import { signInAnonymously, signInWithCustomToken, type Auth } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'
import { auth, firestoreDb, getRealtimeFirebase } from './firebase'
import { api } from '../services/api'
import { getGuestId } from './guestId'

/**
 * #1641 注文をリアルタイムで購読するための認証を用意する。
 *
 * `orders` の Firestore 直読は「当事者だけ」に閉じた。購読する側は、その注文の
 * 当事者であることをトークンのクレームで示す:
 *
 * | 誰 | どのアプリ | クレーム | 出どころ |
 * |---|---|---|---|
 * | ログイン中のお客様 | 既定 | `customerId` | POST /auth/realtime-claim |
 * | ゲスト | 購読専用 (匿名) | `customerId` (= 端末のゲスト ID) | 同上 |
 * | スタッフ | 購読専用 (カスタムトークン) | `staffStoreId` | POST /store-staff/{id}/realtime-token |
 * | オーナー | 既定 | `storeIds` | /store-auth/me が同期 (#192)。ここでは取り直すだけ |
 *
 * **既定のアプリのログインは置き換えない** — 理由は `getRealtimeFirebase` を参照。
 */

/** クレームが乗ったトークンになるまで取り直す (custom claims は取り直さないと反映されない)。 */
async function claimsOf(target: Auth): Promise<Record<string, unknown>> {
  const user = target.currentUser
  if (!user) return {}
  const result = await user.getIdTokenResult()
  return result.claims as Record<string, unknown>
}

/**
 * クレームが**その値になっている**ときだけ何もしない。**在るかどうかで見ない** —
 * 端末に前の人の値が残っていると (ゲスト ID はログアウトで変わる)、ずっと拒否され続けて
 * 黙って API 取得に落ちるため。
 */
async function ensureClaimValue(
  target: Auth,
  key: string,
  expected: string,
  apply: () => Promise<void>,
): Promise<boolean> {
  const user = target.currentUser
  if (!user) return false
  if ((await claimsOf(target))[key] === expected) return true
  await apply()
  await user.getIdToken(true)
  return (await claimsOf(target))[key] === expected
}

/**
 * 購読先と、**その購読で自分の注文を引き当てる customerId**。
 *
 * #1922 値まで返すのは、購読の絞り込みに使う ID が「端末が思っている ID」ではなく
 * **サーバが書き込みに使う ID** でなければならないため (下の `ensureViewerRealtime`)。
 */
export type RealtimeViewer = { db: Firestore; customerId: string }

/**
 * ログイン中のお客様。既定のアプリのまま、customerId クレームだけ足す。
 * 値はサーバが決める (メールから導出) ので、**返ってきた値と一致するまで**確かめる。
 */
export async function ensureCustomerRealtime(): Promise<RealtimeViewer | null> {
  const user = auth.currentUser
  if (!user) return null
  const { customerId } = await api.syncRealtimeClaim()
  if ((await claimsOf(auth))['customerId'] === customerId) return { db: firestoreDb, customerId }
  await user.getIdToken(true)
  return (await claimsOf(auth))['customerId'] === customerId ? { db: firestoreDb, customerId } : null
}

/** ゲスト。購読専用アプリで匿名ログインし、端末のゲスト ID をクレームに入れる。 */
export async function ensureGuestRealtime(): Promise<RealtimeViewer | null> {
  const guestId = getGuestId()
  if (!guestId) return null
  const { auth: rtAuth, db } = getRealtimeFirebase()
  if (!rtAuth.currentUser) await signInAnonymously(rtAuth)
  const ok = await ensureClaimValue(rtAuth, 'customerId', guestId, async () => {
    // apiFetch は未ログインのとき X-Guest-Id を自動で付ける。トークンは
    // 匿名ユーザーのものを明示的に渡す (既定のアプリは未ログインのため)。
    const idToken = await rtAuth.currentUser?.getIdToken()
    // #1922 匿名トークンが取れないまま呼ぶと、apiFetch が既定のログインの
    // トークンを使ってしまい、別人の customerId が返る。黙って落とさず諦める。
    if (!idToken) throw new Error('[realtimeAuth] anonymous token unavailable')
    await api.syncRealtimeClaim(idToken)
  })
  return ok ? { db, customerId: guestId } : null
}

/**
 * #1922 この端末の「見る人」を、**書くときと同じ順番**で決める。
 *
 * `apiFetch` は Firebase のログインがあれば必ずそれで書く (X-Guest-Id より優先) 。
 * 読む側だけが端末のゲスト ID を優先すると、**店舗オーナーでログインした端末から
 * 出した注文が、その端末の一覧に出ない**（書き込みはオーナーの customerId、
 * 購読はゲスト ID で絞る、というねじれになる）。
 */
export async function ensureViewerRealtime(): Promise<RealtimeViewer | null> {
  // 復元中の `currentUser === null` を「未ログイン」と取り違えない
  await auth.authStateReady()
  return auth.currentUser ? ensureCustomerRealtime() : ensureGuestRealtime()
}

/**
 * スタッフ。購読専用アプリでカスタムトークンにサインインする。
 *
 * **店だけでなく本人 (memberId) の一致まで見る。** 共有端末で人が交代したときに
 * 前の人の uid のまま動いてしまい、その人を無効化した時点で (失効が効いて)
 * 交代後の現役スタッフのボードが止まる、という壊れ方をするため。
 */
export async function ensureStaffRealtime(
  storeId: string,
  memberId: string,
): Promise<Firestore | null> {
  const { auth: rtAuth, db } = getRealtimeFirebase()
  const uid = `staff_${storeId}_${memberId}`
  const claims = await claimsOf(rtAuth)
  if (claims.staffStoreId === storeId && rtAuth.currentUser?.uid === uid) return db
  const { customToken } = await api.getStaffRealtimeToken(storeId)
  await signInWithCustomToken(rtAuth, customToken)
  const after = await claimsOf(rtAuth)
  return after.staffStoreId === storeId && rtAuth.currentUser?.uid === uid ? db : null
}

/**
 * オーナー。`storeIds` は /store-auth/me が同期するが、**トークンは取り直さないと
 * 古いまま**なので、その店が載っていなければ 1 度だけ取り直す。
 */
export async function ensureOwnerRealtime(storeId: string): Promise<Firestore | null> {
  const user = auth.currentUser
  if (!user) return null
  const has = (claims: Record<string, unknown>) =>
    Array.isArray(claims.storeIds) && (claims.storeIds as string[]).includes(storeId)
  if (has(await claimsOf(auth))) return firestoreDb
  await user.getIdToken(true)
  return has(await claimsOf(auth)) ? firestoreDb : null
}
